/**
 * Modal de Picking (Separação) via SIAL Web.
 *
 * #MT-9448740: controle de caixas / volumes fica só na Conferência
 * (não na Separação), para evitar conflito entre os dois fluxos.
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';

/** Escape HTML simples. */
function escHtml(v) {
	if (v == null) return '';
	return String(v)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/** Rótulos de status da saída. */
const STATUS_LABEL = {
	10: 'Criada',
	20: 'Separando',
	30: 'Separada',
	40: 'Conferindo',
	50: 'Unpick',
	60: 'Conferida',
	65: 'Faturando',
	70: 'Faturada',
	75: 'Doca Expedição',
	80: 'Expedição',
	90: 'Encerrada',
	100: 'Cancelada',
};

/** Status da saída que permitem separação web. */
const ALLOWED_STATUS = new Set([10, 20, 30, 40, 50]);

/**
 * Abre o modal de Picking (Separação) Web.
 *
 * @param {string} companyId
 * @param {number|string} outputId
 * @param {function} [onSuccess] - chamado após persistência bem-sucedida
 */
export default async function openOutputPickingModal(companyId, outputId, onSuccess) {

	let output;
	try {
		output = await api.call(api.select_output, companyId, outputId);
	} catch (e) {
		notify(e?.message || 'Erro ao carregar dados da saída.', 'Picking');
		return;
	}

	if (!output) {
		notify(`Saída ${outputId} não encontrada.`, 'Picking');
		return;
	}

	if (!ALLOWED_STATUS.has(output.status)) {
		notify(
			output.status >= 60
				? `Saída ${outputId} já conferida (status ${output.status} — ${STATUS_LABEL[output.status] || output.status}). Separação não permitida.`
				: `Status ${output.status} (${STATUS_LABEL[output.status] || '?'}) não permite separação web.`,
			'Picking'
		);
		return;
	}

	let items = output.items || [];

	function buildRowsHtml() {
		if (!items.length) {
			return `<tr><td colspan="8" style="padding:1rem;text-align:center;color:#888">Esta saída não possui itens.</td></tr>`;
		}

		return items.map(item => {
			const num = Number(item.number);
			const sku = item.product_sku ? String(item.product_sku).trim() : '';
			const name = item.product_name ? String(item.product_name).trim() : `Produto #${item.product_id}`;
			const lot = item.lot_number ? String(item.lot_number).trim() : '—';
			const location = item.location_code ? String(item.location_code).trim() : '—';
			const required = Number(item.quantity_required) || 0;
			const supplied = Number(item.quantity_supplied) || 0;
			const pending = Math.max(0, required - supplied);
			const inputDisabled = pending <= 0;

			return `
				<tr data-num="${num}" style="${inputDisabled ? 'opacity:0.6' : ''}">
					<td style="padding:5px 7px;color:#888;font-size:0.82rem;white-space:nowrap;vertical-align:middle">${num}</td>
					<td style="padding:5px 7px;font-size:0.8rem;white-space:nowrap;vertical-align:middle">${escHtml(location)}</td>
					<td style="padding:5px 7px;min-width:160px;vertical-align:middle">
						<div style="font-size:0.7rem;color:#666">${escHtml(sku)}</div>
						<div style="font-size:0.84rem">${escHtml(name)}</div>
					</td>
					<td style="padding:5px 7px;white-space:nowrap;font-size:0.82rem;vertical-align:middle">${escHtml(lot)}</td>
					<td style="padding:5px 7px;text-align:right;font-weight:500;vertical-align:middle">${required}</td>
					<td style="padding:5px 7px;text-align:right;color:#555;vertical-align:middle">${supplied}</td>
					<td style="padding:5px 7px;text-align:right;color:${pending > 0 ? '#c80' : '#2a7'};font-weight:500;vertical-align:middle">${pending}</td>
					<td style="padding:5px 7px;vertical-align:middle">
						<input type="number"
							class="pick-qty-input"
							data-num="${num}"
							data-pending="${pending}"
							data-supplied="${supplied}"
							data-required="${required}"
							min="0" max="${pending}" step="1" value="0"
							${inputDisabled ? 'disabled' : ''}
							style="width:5rem;text-align:right;padding:2px 5px;${inputDisabled ? 'background:#f5f5f5' : ''}">
					</td>
				</tr>`;
		}).join('');
	}

	function collectEntries(node) {
		const inputs = Array.from(node.querySelectorAll('.pick-qty-input:not([disabled])'));
		const entries = [];

		for (const inp of inputs) {
			const raw = inp.value.trim();
			if (!raw || raw === '0') continue;
			const qty = parseInt(raw, 10);
			if (isNaN(qty) || qty <= 0) continue;

			const num = parseInt(inp.dataset.num, 10);
			const pending = parseInt(inp.dataset.pending, 10);
			if (qty > pending) {
				return { error: `Linha ${num}: quantidade ${qty} ultrapassa o pendente (${pending}).`, inp };
			}

			const supplied = parseInt(inp.dataset.supplied, 10);
			entries.push({
				num,
				qty,
				newSupplied: supplied + qty,
			});
		}
		return { entries };
	}

	async function patchOutputSupplied(entries) {
		const itemsUpdated = items.map(item => {
			const entry = entries.find(e => e.num === Number(item.number));
			return {
				number: item.number,
				location_id: item.location_id,
				warehouse_id: item.warehouse_id,
				product_id: item.product_id,
				lot_id: item.lot_id,
				quantity_required: item.quantity_required,
				quantity_supplied: entry ? entry.newSupplied : (item.quantity_supplied ?? null),
				discount: item.discount ?? 0,
				unit_cost: 0,
				unit_price: 0,
			};
		});

		const patchPayload = {
			order_type: output.order_type,
			order_id: output.order_id,
			warehouse_company_id: output.warehouse_company_id,
			warehouse_id: output.warehouse_id,
			volume_type: output.volume_type,
			volumes: output.volumes,
			weight: output.weight,
			status: output.status === 10 ? 20 : output.status,
			items: itemsUpdated,
		};

		const patchRes = await api.call(api.patch_output, companyId, outputId, patchPayload);
		if (patchRes?.error) throw new Error(patchRes.error);
	}

	const statusLabel = STATUS_LABEL[output.status] || String(output.status);
	const pickerInfo = output.picked_by
		? `<span style="color:#555;font-size:0.8rem">Separador: <strong>${escHtml(output.picked_by)}</strong></span>`
		: '';

	const html = `
		<div class="msgbox" style="min-width:700px;max-width:1000px">
			<div class="mb-title">Picking — Saída #${escHtml(String(outputId))}</div>
			<div class="mb-text" style="max-height:80vh;overflow-y:auto">
				<div style="display:flex;flex-wrap:wrap;gap:1rem;margin-bottom:0.6rem;font-size:0.85rem;color:#444">
					<span>Pedido: <strong>${escHtml(String(output.order_id || '—'))}</strong></span>
					<span>Status: <strong>${escHtml(statusLabel)}</strong></span>
					${output.reference ? `<span>Ref.: <strong>${escHtml(output.reference)}</strong></span>` : ''}
					${pickerInfo}
				</div>
				<p style="margin:0 0 0.4rem;font-size:0.8rem;color:#888">
					Informe a quantidade em <strong>Separar agora</strong> e clique <strong>Salvar</strong>.
					Abertura, preenchimento e lacre de caixas ficam na <strong>Conferência</strong>.
				</p>
				<div style="overflow-x:auto">
					<table id="pick-items-table" style="width:100%;border-collapse:collapse;font-size:0.86em">
						<thead>
							<tr style="background:#f0f0f0">
								<th style="padding:5px 7px;text-align:left">Linha</th>
								<th style="padding:5px 7px;text-align:left">Posição</th>
								<th style="padding:5px 7px;text-align:left">Produto</th>
								<th style="padding:5px 7px;text-align:left">Lote sep.</th>
								<th style="padding:5px 7px;text-align:right">Req.</th>
								<th style="padding:5px 7px;text-align:right">Já sep.</th>
								<th style="padding:5px 7px;text-align:right">Pendente</th>
								<th style="padding:5px 7px;text-align:right">Separar agora</th>
							</tr>
						</thead>
						<tbody>${buildRowsHtml()}</tbody>
					</table>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y" id="pick-save-btn">Salvar</button>
		</div>`;

	const dlg = dialog.create(html, (key) => {
		if (key === 'Escape' || key === 'N') dlg.destroy();
	}, /Escape|N/);

	const node = dlg.getNode();

	node.querySelector('#pick-save-btn')?.addEventListener('click', async () => {
		const collected = collectEntries(node);
		if (collected.error) {
			notify(collected.error, 'Picking');
			collected.inp?.focus();
			return;
		}
		const { entries = [] } = collected;

		if (!entries.length) {
			dlg.destroy();
			if (typeof onSuccess === 'function') onSuccess();
			return;
		}

		const saveBtn = node.querySelector('#pick-save-btn');
		saveBtn.disabled = true;
		saveBtn.textContent = 'Salvando…';

		try {
			await patchOutputSupplied(entries);
			dlg.destroy();
			notify(`Separação da saída #${outputId} registrada com sucesso.`, 'Picking');
			if (typeof onSuccess === 'function') onSuccess();
		} catch (e) {
			notify(e?.message || String(e), 'Erro');
		} finally {
			saveBtn.disabled = false;
			saveBtn.textContent = 'Salvar';
		}
	});
}
